import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, auth as authApi } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { isCrossHost, safeRedirect } from "@/lib/safeRedirect";

/**
 * Register page — creates a Skarp account via our backend proxy.
 *
 * Skarp's `/api/auth/register` flow normally requires email verification:
 *   1. POST email + password (+ optional username) → Skarp queues a verification email
 *   2. We respond with `requires_verification: true` (no session cookie set)
 *   3. SPA shows a "check your email" success state and a link back to /login
 *
 * If Skarp ever returns an immediately usable session (e.g. dev configs that skip
 * verification), `requires_verification` will be false and the auth context's
 * `refresh()` picks up the cookie that the backend already wrote.
 *
 * The optional `?from=` query param threads back through to /login → after-login
 * redirect, so a user who clicked "Create one" from a deep link still lands where
 * they were heading.
 */

export function Register() {
  const { refresh, state } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirect(searchParams.get("from"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Success state once Skarp has accepted the registration. We hold the email
  // so the verification message can address the user with the address they used.
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  // If the user is already authenticated (e.g. they hit /register while signed
  // in), bounce them to the redirect target rather than letting them re-enroll.
  // Cross-host destinations need window.location (react-router can't handle).
  useEffect(() => {
    if (state.status === "authenticated") {
      if (isCrossHost(redirectTo)) {
        window.location.href = redirectTo;
      } else {
        navigate(redirectTo, { replace: true });
      }
    }
  }, [state.status, navigate, redirectTo]);

  // Light client-side validation so we can flag obvious issues before round-tripping.
  const validate = (): string | null => {
    if (!email.trim()) return "Email is required.";
    if (!/.+@.+\..+/.test(email.trim())) return "That email doesn't look right.";
    if (!password) return "Password is required.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (password !== confirm) return "Passwords don't match.";
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setBusy(true);
    try {
      const r = await authApi.register({
        email: email.trim(),
        password,
        username: username.trim() || undefined,
      });
      if (r.requires_verification) {
        setSubmittedEmail(email.trim());
      } else {
        // Skarp returned a session straight away — refresh auth and let the
        // useEffect above do the redirect.
        await refresh();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setError(
          body?.error ??
            body?.detail ??
            (err.status === 409
              ? "An account with that email already exists."
              : `Couldn't create the account (${err.status}).`),
        );
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  // Success state — verification email sent.
  if (submittedEmail) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950">
        <header className="border-b border-zinc-200 dark:border-zinc-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <Link to="/" className="font-semibold tracking-tight text-lg">
              ← Coauthor
            </Link>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md">
            <h1 className="text-2xl font-semibold mb-2">Check your email</h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
              We sent a verification link to{" "}
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                {submittedEmail}
              </span>
              . Click it to activate your account, then come back to sign in.
            </p>
            <p className="text-xs text-zinc-500 mb-6">
              Didn't get it? Check your spam folder, or wait a minute and try again.
              The verification link is sent by Skarp (our identity provider) on
              behalf of Coauthor.
            </p>
            <Link
              to={`/login${redirectTo !== "/" ? `?from=${encodeURIComponent(redirectTo)}` : ""}`}
              className="inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium hover:opacity-90"
            >
              Continue to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-semibold tracking-tight text-lg">
            ← Coauthor
          </Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-semibold mb-2">Create your account</h1>
          <p className="text-sm text-zinc-500 mb-6">
            One account works across Coauthor and other Skarp apps.
          </p>

          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">
                Username <span className="text-xs text-zinc-500 font-normal">(optional)</span>
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                placeholder="A short handle other people see"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Confirm password</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="Repeat your password"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              />
            </label>
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Creating account…" : "Create account"}
            </button>
          </form>

          <div className="border-t border-zinc-200 dark:border-zinc-800 mt-6 pt-4 text-sm text-zinc-500">
            Already have an account?{" "}
            <Link
              to={`/login${redirectTo !== "/" ? `?from=${encodeURIComponent(redirectTo)}` : ""}`}
              className="text-zinc-900 dark:text-zinc-100 underline hover:opacity-80"
            >
              Sign in →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
